// src/utils/metricDefinitions.js
//
// Two small maps on the organization document, written by its admins (the
// org write rule) and read by everyone in the org through OrganizationContext:
//
//   metricDefinitions.{metricId} = { answers: { bendId: option }, updatedAt, updatedBy }
//     The intake answers for a figure (absence with or without approved leave,
//     headcount or FTE, contractors in or out): asked once, kept with the
//     figure, shown wherever it is compared.
//
//   dataRequests.{systemKey} = { requestedAt, requestedBy, note }
//     A request to connect a system (HRIS, payroll, absence, safety, benefits,
//     talent). A request is a request: the console never shows a connection
//     that does not exist.
//
//   intakeMappings.{normHeader} = { metricId, header, savedAt, savedBy }
//     How a column in the organization's own words maps to a dictionary id,
//     saved the first time a wide file is read so next month's file maps
//     itself. Keyed by the same header normalisation the CSV module uses.

import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { normHeader } from './workforceCsv';

const who = (user) => (user && (user.email || user.uid)) || null;

export const readMetricDefinitions = (orgData) => (orgData && orgData.metricDefinitions) || {};

export const saveMetricDefinition = async (orgId, metricId, answers, user) => {
    if (!orgId) throw new Error('No organization selected.');
    const clean = {};
    Object.entries(answers || {}).forEach(([k, v]) => { if (v) clean[k] = String(v); });
    const value = { answers: clean, updatedAt: new Date().toISOString(), updatedBy: who(user) };
    await updateDoc(doc(db, 'organizations', orgId), { [`metricDefinitions.${metricId}`]: value });
    return value;
};

export const readDataRequests = (orgData) => (orgData && orgData.dataRequests) || {};

export const requestConnection = async (orgId, systemKey, user, note = '') => {
    if (!orgId) throw new Error('No organization selected.');
    const value = { requestedAt: new Date().toISOString(), requestedBy: who(user), note: String(note || '') };
    await updateDoc(doc(db, 'organizations', orgId), { [`dataRequests.${systemKey}`]: value });
    return value;
};

export const withdrawRequest = async (orgId, systemKey) => {
    if (!orgId) throw new Error('No organization selected.');
    await updateDoc(doc(db, 'organizations', orgId), { [`dataRequests.${systemKey}`]: deleteField() });
};

// --- column mappings for wide files ---

export const readIntakeMappings = (orgData) => (orgData && orgData.intakeMappings) || {};

// The saved mappings flattened to what validateRows takes: { [normHeader]: metricId }.
export const mappingsForValidate = (saved) => {
    const out = {};
    Object.entries(saved || {}).forEach(([k, v]) => {
        const id = v && typeof v === 'object' ? v.metricId : v;
        if (k && id) out[k] = String(id);
    });
    return out;
};

export const saveIntakeMapping = async (orgId, header, metricId, user) => {
    if (!orgId) throw new Error('No organization selected.');
    const key = normHeader(header);
    if (!key || !metricId) throw new Error('A column and a figure are needed to save a mapping.');
    const value = { metricId: String(metricId), header: String(header || '').trim(), savedAt: new Date().toISOString(), savedBy: who(user) };
    await updateDoc(doc(db, 'organizations', orgId), { [`intakeMappings.${key}`]: value });
    return value;
};

export const removeIntakeMapping = async (orgId, header) => {
    if (!orgId) throw new Error('No organization selected.');
    const key = normHeader(header);
    if (!key) return;
    await updateDoc(doc(db, 'organizations', orgId), { [`intakeMappings.${key}`]: deleteField() });
};
